import {
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { Button } from "antd";
import React from "react";

import {
  formatAmount,
  getOverEstimateAmount
} from "@/components/forecasting/forecastTotals";

const EstLabel = ({
  actual,
  estimated
}: {
  actual: number;
  estimated: number;
}) => {
  if (estimated <= 0) return null;
  const isOver = getOverEstimateAmount(actual, estimated) > 0;
  return (
    <span className={isOver ? "text-amber-500 font-medium" : ""}>
      {" "}
      / Est: £{formatAmount(estimated)}
      {isOver && <ExclamationCircleOutlined className="ml-1" />}
    </span>
  );
};

const OverEstimateWarning = ({
  actual,
  estimated
}: {
  actual: number;
  estimated: number;
}) => {
  const overBy = getOverEstimateAmount(actual, estimated);
  if (overBy <= 0) return null;
  return (
    <span className="text-xs font-medium text-amber-600 flex items-center gap-1">
      <ExclamationCircleOutlined />
      Over estimate by £{formatAmount(overBy)}
    </span>
  );
};

/** Collapsible card shared by the Labour, Plant, Materials and Subcontractors cost sections. */
export const CostSection = ({
  title,
  icon,
  headerClassName,
  itemCount,
  total,
  estimated,
  expanded,
  onToggle,
  addButton,
  children
}: {
  title: string;
  icon: React.ReactNode;
  /** Background tint of the header, e.g. `bg-blue-50`. */
  headerClassName: string;
  itemCount: number;
  total: number;
  estimated: number;
  expanded: boolean;
  onToggle: () => void;
  addButton?: { label: string; onClick: () => void; loading?: boolean };
  children: React.ReactNode;
}) => (
  <section className="bg-white border border-solid border-[#E5E7EB] rounded-xl mb-5 overflow-hidden">
    <div
      className={`${headerClassName} px-4 py-3 flex items-center justify-between cursor-pointer`}
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <h3 className="m-0 text-sm font-semibold text-gray-900">{title}</h3>
          <div className="text-xs text-gray-500">
            {itemCount} item{itemCount !== 1 ? "s" : ""} • Total: £
            {formatAmount(total)}
            <EstLabel actual={total} estimated={estimated} />
          </div>
        </div>
      </div>
      <Button
        type="text"
        aria-label={`${expanded ? "Collapse" : "Expand"} ${title}`}
        aria-expanded={expanded}
        icon={
          expanded ? (
            <MinusCircleOutlined className="text-gray-400" />
          ) : (
            <PlusCircleOutlined className="text-gray-400" />
          )
        }
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      />
    </div>

    {expanded && (
      <>
        {children}

        {addButton && (
          <div className="px-4 py-3 border-0 border-t border-solid border-gray-200">
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              loading={addButton.loading}
              onClick={addButton.onClick}
              block
              className="h-10"
            >
              {addButton.label}
            </Button>
          </div>
        )}

        <div className="px-4 py-3 bg-gray-50 border-0 border-t border-solid border-gray-200 flex justify-between items-center">
          <span className="font-medium text-gray-700">{title} Subtotal</span>
          <div className="flex items-center gap-3">
            <OverEstimateWarning actual={total} estimated={estimated} />
            {estimated > 0 && (
              <span className="text-sm text-gray-400">
                Est: £{formatAmount(estimated)}
              </span>
            )}
            <span className="font-bold text-gray-900 text-lg">
              £{formatAmount(total)}
            </span>
          </div>
        </div>
      </>
    )}
  </section>
);
